import React from "react";
import {Check} from "@sparkware/app-utils";
import {observer} from 'mobx-react';
import {OrbitComponent} from '../../../../../app-react/components/base-components/orbit-component/orbit-component';
import {LiveCasinoGridBox} from "./boxes/grid/live-casino-grid.box";
import {observable, action} from "mobx";
import {LiveCasinoGridItemBox} from "./boxes/grid-item/live-casino-grid-item.box";
import {LiveCasinoArenaGridItem} from './live-casino-arena-grid-item.component';
import {LiveCasinoGridItemSkeletonBox} from "./boxes/grid/skeleton/live-casino-grid-item-skeleton.box";
import {LiveCasinoGridItemSkeletonTitleBox} from "./boxes/grid/skeleton/live-casino-grid-item-skeleton-title.box";
import {LiveCasinoGridItemSkeletonWrapperBox} from "./boxes/grid/skeleton/live-casino-grid-item-skeleton-wrapper.box";
import {HeaderFilterConfiguration, LiveCasinoPopularTableConfiguration, LiveCasinoPromotionalBannerConfiguration, NewLiveCasinoConfiguration} from '../../../../../app-react/services/configurations/config-data-sections/arenas.configuration';
import {BannerComponent} from "../../../banner/banner.component";

export interface ILiveCasinoArenaGridProps {
  seoCategory: string;
  tablesConfigs: NewLiveCasinoConfiguration;
  useAISortedList: boolean;
  promotionalBannerConfig?: LiveCasinoPromotionalBannerConfiguration;
}

@observer
export class LiveCasinoArenaGrid extends OrbitComponent<ILiveCasinoArenaGridProps> {

    private _gameElementRef: HTMLDivElement | null = null;
    private _gridElementRef: HTMLDivElement | null = null;

    private _cmsBannerRendered: boolean = false;

    @observable.ref
    private _cmsBannerPosition: number = 0;

    @observable.ref
    private _numberOfGamesPerRow: number = 0;

    private _captureGameElementRef = (gameElement: HTMLDivElement | null) => {
        this._gameElementRef = gameElement;
        this._computeNumberOfGamesInARow();
    };

    private _captureGridElementRef = (gameElement: HTMLDivElement | null) => {
        this._gridElementRef = gameElement;
        this._computeNumberOfGamesInARow();
    };

    @action.bound
    private _setCMSBannerPosition() {
        this._cmsBannerPosition = this._numberOfGamesPerRow * this.props?.promotionalBannerConfig?.displayRowPlace;
    }

    private _prepareGames(tableConfig: LiveCasinoPopularTableConfiguration): number[] {

        let gameIds = this.services.liveTableFeeds.getLiveGames(tableConfig.gamesId, {
            useAISortedList: tableConfig.useAISortedList,
            numberOfSortingExemptGames: tableConfig.ignoreNumberOfGamesInAISorting
        });

        try {

            const arenas = this.props.tablesConfigs.arenas;

            gameIds = this.services.liveTableFeeds.moveGamesThatOpenSoonToTheEnd(gameIds);

            if (Check.isArray(arenas) &&  arenas.length > 0) {
                gameIds = gameIds.slice(0, this._getCurrentSubArenaConfig(arenas).maxGamesToShow);
            }

            return gameIds;

        } catch (error) {
            this.services.logger.error("Failed to reduce to max games", error);
            return gameIds;
        }
    }

    private _computeNumberOfGamesInARow() {

        if (!this._gridElementRef || !this._gameElementRef) return;

        try {
            this._numberOfGamesPerRow = Math.round(Number(this._gridElementRef["offsetWidth"]) / Number(this._gameElementRef["offsetWidth"]));
            this._setCMSBannerPosition();
        } catch (error) {
            this.services.logger.error("Failed to compute number of live games in a row", error);
        }
    }

    private _getCurrentSubArenaConfig(filtersConfigurations: HeaderFilterConfiguration[]): HeaderFilterConfiguration {
        try {
            if (this.services.gamesFilter.isFilterOn) {
                return filtersConfigurations.filter(config => this.services.gamesFilter.isFilterApplied(config.filter))[0];
            } else {
                return filtersConfigurations.filter(config => Check.isNullOrUndefined(config.filter))[0];
            }

        } catch (error) {
            this.services.logger.error("Failed to get current sub arena config", error);
            return null;
        }
    }

    private _getPromotionalBanner(arenas: HeaderFilterConfiguration[]) {

        const currentArena = this._getCurrentSubArenaConfig(arenas);

        if (currentArena?.banners?.promotionalBanner) {
            return currentArena.banners.promotionalBanner;
        } else {
            return this.props.promotionalBannerConfig;
        }
    }

    private _renderPromotionalBanner() {

        if (Check.isNullOrUndefined(this.props.promotionalBannerConfig)) return null;

        this._cmsBannerRendered = true;
        const promotionalBanner = this._getPromotionalBanner(this.props.tablesConfigs.arenas);

        return (
            <BannerComponent
                templateID={promotionalBanner.templateID}
                componentID={promotionalBanner.componentID}
                bannerAdminPlaceholderId={promotionalBanner.bannerAdminPlaceholderId}
                showPlaceholder={false}
                fallbackComponent={""}
            />
        );
    }

    private _renderGridSkeleton() {

        let skeletons = [];

        for (var i = 0; i < 12; i++) {
            skeletons.push(
                <LiveCasinoGridItemBox key={i}>
                    <LiveCasinoGridItemSkeletonWrapperBox>
                        <LiveCasinoGridItemSkeletonBox/>
                        <LiveCasinoGridItemSkeletonTitleBox>Loading</LiveCasinoGridItemSkeletonTitleBox>
                    </LiveCasinoGridItemSkeletonWrapperBox>
                </LiveCasinoGridItemBox>
            );
        }

        return skeletons;
    }

    private _renderGridItem(tableId: number, tableConfig: LiveCasinoPopularTableConfiguration, gameIndex: number, key: number) {

        const listTitle = this.services.liveTableFeeds.currentFilter?.label ?? tableConfig.title;
        const shouldRenderPromoBanner = gameIndex === (this._cmsBannerPosition - 1);

        return (
            <React.Fragment key={key}>
                <LiveCasinoArenaGridItem
                    tableId={tableId}
                    gameInfo={this.services.games.gamesMap[tableId]}
                    gameIndex={gameIndex}
                    listTitle={listTitle}
                    liveTable={this.services.liveTableFeeds.getTableInfo(tableId)}
                    seoCategory={this.props.seoCategory}
                    captureGameElementRef={this._captureGameElementRef}
                />
                {
                    shouldRenderPromoBanner && this._renderPromotionalBanner()
                }
            </React.Fragment>
        );
    }

    private _renderArenaGrid(tableConfig: LiveCasinoPopularTableConfiguration) {

        let gameIds = this._prepareGames(tableConfig);

        if (gameIds.length === 0) {
            return this._renderGridSkeleton();
        } else {
            return gameIds.map((tableId, gameIndex) => this._renderGridItem(tableId, tableConfig, gameIndex, tableId));
        }
    }

    componentDidMount() {
        super.componentDidMount();

        this.reaction(() => [
            this.services.device.os.isPC,
            this.services.device.isTablet,
            this.services.device.isMobile
        ], () => this._setCMSBannerPosition(), {fireImmediately: true});
    }

    render() {

        this._cmsBannerRendered = false;

        return (
            <LiveCasinoGridBox ref={this._captureGridElementRef} className="cy-live-casino-grid">
                {
                    this.props.tablesConfigs.popularTables.map((tableConfig) => {
                        return this._renderArenaGrid(tableConfig)
                    })
                }
                {
                    !this._cmsBannerRendered && this._renderPromotionalBanner()
                }
            </LiveCasinoGridBox>
        );
    }
}
